import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('installPromptDismissed');
    const installed = window.matchMedia('(display-mode: standalone)').matches;
    
    if (dismissed || installed) return;

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // Show prompt after 3 seconds for iOS (can't capture beforeinstallprompt)
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-4 shadow-2xl border border-violet-400/20">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-white/70 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-sm">
              Install SharePay App
            </h3>
            <p className="text-white/80 text-xs mt-0.5">
              {isIOS 
                ? "Tap Share → 'Add to Home Screen' for app-like experience"
                : "Add to home screen for quick access & offline support"
              }
            </p>
            
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstall}
                size="sm"
                className="mt-2 bg-white text-violet-600 hover:bg-white/90 rounded-full text-xs h-8 px-4"
              >
                <Download className="w-3 h-3 mr-1" />
                Install Now
              </Button>
            )}
            
            {isIOS && (
              <div className="mt-2 flex items-center gap-2 text-white/90 text-xs">
                <span>Tap</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L12 14M12 2L8 6M12 2L16 6M4 14H20V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V14Z"/>
                </svg>
                <span>then "Add to Home Screen"</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
